import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { ref, get } from 'firebase/database';
import { db, database } from '../utils/firebase';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { format } from 'date-fns';

const AdminAIChat = () => {
  const [chatSessions, setChatSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionMessages, setSessionMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    fetchChatSessions();
  }, []);

  const fetchChatSessions = async () => {
    try {
      setLoading(true);
      
      const sessionsQuery = query(
        collection(db, 'aiChatSessions'),
        orderBy('startTime', 'desc')
      );
      
      const snapshot = await getDocs(sessionsQuery);
      const sessions = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setChatSessions(sessions);
    } catch (error) {
      console.error('Error fetching chat sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSessionMessages = async (chatId) => {
    try {
      const chatRef = ref(database, `aiChats/${chatId}/messages`);
      const snapshot = await get(chatRef);
      
      if (snapshot.exists()) {
        const messages = Object.values(snapshot.val()).sort((a, b) => a.timestamp - b.timestamp);
        setSessionMessages(messages);
      } else {
        setSessionMessages([]);
      }
    } catch (error) {
      console.error('Error fetching session messages:', error);
      setSessionMessages([]);
    }
  };

  const handleSessionSelect = async (session) => {
    setSelectedSession(session);
    await fetchSessionMessages(session.chatId);
  };

  const getSessionStatus = (session) => {
    const now = new Date();
    const lastActivity = session.lastActivity?.toDate?.() || new Date(session.lastActivity);
    const timeDiff = now - lastActivity;
    
    if (timeDiff < 5 * 60 * 1000) { // 5 minutes
      return { status: 'active', color: 'text-green-400', label: 'Active' };
    } else if (timeDiff < 24 * 60 * 60 * 1000) { // 24 hours
      return { status: 'recent', color: 'text-yellow-400', label: 'Recent' };
    } else {
      return { status: 'old', color: 'text-gray-400', label: 'Old' };
    }
  };

  const filteredSessions = chatSessions.filter(session => {
    if (filter === 'all') return true;
    if (filter === 'active') return getSessionStatus(session).status === 'active';
    if (filter === 'escalated') return session.escalated;
    return session.userRole === filter;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-400 mx-auto"></div>
          <p className="mt-4 text-gray-300">Loading AI chat sessions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <Header />
      
      <div className="p-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin')}
            className="flex items-center gap-2 text-green-300 hover:text-green-400 mb-4"
          >
            ← Back to Admin Dashboard
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">AI Chat Management</h1>
              <p className="text-gray-300">Monitor and manage AI chat sessions</p>
            </div>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-4 py-2 border border-gray-700 rounded-md focus:ring-2 focus:ring-green-400 bg-gray-800 text-white"
            >
              <option value="all">All Sessions</option>
              <option value="active">Active Sessions</option>
              <option value="escalated">Escalated</option>
              <option value="host">Host Sessions</option>
              <option value="venue">Venue Sessions</option>
              <option value="admin">Admin Sessions</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Sessions List */}
          <div className="lg:col-span-1">
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h2 className="text-xl font-bold text-white mb-4">Chat Sessions ({filteredSessions.length})</h2>
              
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {filteredSessions.map((session) => {
                  const status = getSessionStatus(session);
                  return (
                    <div
                      key={session.id}
                      onClick={() => handleSessionSelect(session)}
                      className={`p-3 rounded-lg cursor-pointer transition-colors border ${
                        selectedSession?.id === session.id
                          ? 'bg-green-900/20 border-green-500'
                          : 'bg-gray-700 border-gray-600 hover:bg-gray-600'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="text-sm font-medium text-white">
                          {session.userEmail}
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${status.color} bg-gray-700`}>
                          {status.label}
                        </span>
                      </div>
                      
                      <div className="text-xs text-gray-400 mb-1">
                        Role: {session.userRole}
                      </div>
                      
                      <div className="text-xs text-gray-400">
                        Messages: {session.messageCount}
                      </div>
                      
                      <div className="text-xs text-gray-500 mt-1">
                        {session.startTime?.toDate?.() ? 
                          format(session.startTime.toDate(), 'MMM d, h:mm a') : 
                          'Recently'
                        }
                      </div>
                    </div>
                  );
                })}
                
                {filteredSessions.length === 0 && (
                  <div className="text-center py-8">
                    <div className="text-4xl mb-2">💬</div>
                    <p className="text-gray-400">No chat sessions found</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Chat Messages */}
          <div className="lg:col-span-2">
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              {selectedSession ? (
                <>
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold text-white">Chat Messages</h2>
                    <div className="text-sm text-gray-400">
                      {selectedSession.userEmail} ({selectedSession.userRole})
                    </div>
                  </div>
                  
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {sessionMessages.map((message) => (
                      <div
                        key={message.id}
                        className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-xs px-3 py-2 rounded-lg ${
                            message.sender === 'user'
                              ? 'bg-green-600 text-white'
                              : 'bg-gray-700 text-gray-200'
                          }`}
                        >
                          <p className="text-sm">{message.text}</p>
                          <p className="text-xs opacity-70 mt-1">
                            {format(new Date(message.timestamp), 'h:mm a')}
                          </p>
                        </div>
                      </div>
                    ))}
                    
                    {sessionMessages.length === 0 && (
                      <div className="text-center py-8">
                        <p className="text-gray-400">No messages found for this session</p>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <div className="text-4xl mb-4">💬</div>
                  <h3 className="text-lg font-semibold text-white mb-2">Select a Chat Session</h3>
                  <p className="text-gray-400">Choose a session from the list to view messages</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Analytics Summary */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="text-2xl font-bold text-white">{chatSessions.length}</div>
            <div className="text-gray-400 text-sm">Total Sessions</div>
          </div>
          
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="text-2xl font-bold text-green-400">
              {chatSessions.filter(s => getSessionStatus(s).status === 'active').length}
            </div>
            <div className="text-gray-400 text-sm">Active Sessions</div>
          </div>
          
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="text-2xl font-bold text-blue-400">
              {chatSessions.filter(s => s.userRole === 'host').length}
            </div>
            <div className="text-gray-400 text-sm">Host Sessions</div>
          </div>
          
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="text-2xl font-bold text-purple-400">
              {chatSessions.filter(s => s.userRole === 'venue').length}
            </div>
            <div className="text-gray-400 text-sm">Venue Sessions</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAIChat; 